import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { ReactP5Wrapper } from "react-p5-wrapper";
import { useState, useEffect, useRef, useCallback } from "react";
import sketch from "./orbitsSketch";
import sketch2 from "./mazeSketch";

const imdb = "https://www.imdb.com/name/nm3081444/";
const toldStories = "https://photo.myked.com/#motion";
const photosLink = "https://photo.myked.com/#photography";
const interactiveExperiences = "https://dontgetstabbed.online";
const whimsical = "https://myke.wtf/adafruit_interview/";

// Image descriptions for alt text
const imageDescriptions = {
  'aerials_01.jpg': 'Aerial view of the University of Oklahoma campus in Oklahoma City, captured by drone at sunset with green quads and urban skyline in the distance',
  'aerials_02.jpg': 'Aerial photo of Duluth, Minnesota, showing the canal and iconic lift bridge at sunset, with breakwaters extending into Lake Superior',
  'aerials_03.jpg': 'Aerial view over the train yard on Minnesota Island in Duluth, Minnesota, with dozens of railcars and early spring foliage',
  'aerials_04.jpg': 'Drone shot over Lake Superior near Duluth, Minnesota, featuring a lone sailboat gliding across rippling blue water',
  'aerials_05.jpg': 'Aerial view of a northern Minnesota airfield at sunset, showing runway 24 illuminated with red and blue lights, bordered by trees and grassy terrain',
  
  'food_01.jpg': 'A close-up of a glass of Coca-Cola with visible carbonation and a foamy head, with the words "Enjoy Coca-Cola" on the glass',
  'food_02.jpg': 'A fresh Caprese-style salad of mozzarella, tomato, avocado, and herbs on a white plate, being lightly drizzled with dressing in a modern restaurant setting',
  'food_03.jpg': 'Crispy fried wontons plated with a colorful vegetable medley, accompanied by a pint glass with a stylized "F" logo in the background',
  'food_04.jpg': 'Close-up of perfectly seared scallops served on a creamy sauce with a dark balsamic drizzle, topped with crushed pistachios and plated beside sautéed greens',
  'food_05.jpg': 'Slices of green tomatoes coated in flour, ready for frying, arranged across a lightly dusted surface',
  'food_06.jpg': 'Several pieces of beef roulade tied with butcher\'s twine, resting on a cooling rack after searing, showcasing a rich, browned crust',
  'food_07.jpg': 'A gourmet entrée featuring sliced blackened steak, roasted sweet potato purée, grilled asparagus, and a single scallop, all artfully plated on a white dish',
  'food_08.jpg': 'Overhead view of a fresh arugula salad with golden and red beets, candied pecans, and grated cheese, served on a large white plate with a glass of white wine',
  'food_09.jpg': 'Rows of chocolate-topped éclairs filled with cream, arranged neatly on a parchment-lined baking tray',
  'food_10.jpg': 'Small white fish fillets coated in flour, resting in a metal strainer basket, prepared for frying',
  
  'outside_01.jpg': 'Sunrise on Lake Superior, with large stones in the water and the golden sun casting reflections across the calm surface',
  'outside_02.jpg': 'Silhouette of a person on a rocky ledge overlooking Lake Superior at sunrise, with a gradient sky from orange to blue',
  'outside_03.jpg': 'Iconic desert butte formation under a partly cloudy sky in Monument Valley, casting a dramatic shadow across the red sandstone landscape',
  'outside_04.jpg': 'A person in a red jacket stands on a misty mountain ledge overlooking an expansive valley and distant peaks under a dramatic cloudy sky',
  'outside_05.jpg': 'Sunlit winter scene of a snow-covered field bordered by a rustic fence and leafless trees, with golden light filtering through morning fog',
  'outside_06.jpg': 'A long road stretches toward the horizon through a vast, open desert landscape under a bright blue sky with scattered clouds',
  'outside_07.jpg': 'A dark, moody night photo of pine trees silhouetted against a green and purple aurora dancing across the starry sky',
  'outside_08.jpg': 'A black and white image of a person standing in a calm lake at sunrise, holding a camera and facing the horizon, creating a quiet reflective mood',
  
  'people_01.jpg': 'A close-up portrait of a man with glasses, framed by a dramatic natural backdrop, captured in golden hour light with warm tones',
  'people_02.jpg': 'A candid street-style photo of a man sitting on a stoop, smiling at the camera with casual confidence, wearing a dark hoodie and jeans',
  'people_03.jpg': 'Portrait of a woman in natural light, partially framed by foliage, with a soft and contemplative expression and shallow depth of field',
  
  'wedding_01.jpg': 'An emotional moment as a bride and groom hold each other under soft string lights at night, surrounded by guests in an intimate outdoor setting',
  'wedding_02.jpg': 'A bride and groom walk hand-in-hand through a forested path surrounded by tall pines, with golden sunlight filtering through the trees',
  'wedding_03.jpg': 'Candid moment of the bride laughing as the groom dips her for a kiss in front of a colorful mural, blending playfulness with urban flair',
  'wedding_04.jpg': 'A black and white photo of a wedding couple sharing a quiet, close moment with their foreheads touching under soft lighting',
  'wedding_05.jpg': 'A groom lifts his bride in a joyful twirl in front of a red barn, her dress flowing mid-air as she laughs with her arms around him',
  'wedding_06.jpg': 'The couple shares their first dance under a canopy of warm string lights, surrounded by guests in an outdoor reception at dusk',
  'wedding_07.jpg': 'A wedding party celebrates with a champagne toast, with glasses raised and joyful expressions captured mid-laughter'
};

// Helper function to get alt text for images
const getPhotoCategory = (url) => {
  const filename = url.split('/').pop().replace('_t.jpg', '');
  return imageDescriptions[filename] || 'Photography by Myke D';
};

// Add your photo URLs here - replace with actual URLs from photo.myked.com
const photoUrls = [
  "/images/outside_01_t.jpg",
  "/images/outside_02_t.jpg", 
  "/images/outside_03_t.jpg",
  "/images/outside_04_t.jpg",
  "/images/outside_05_t.jpg",
  "/images/outside_06_t.jpg",
  "/images/outside_07_t.jpg",
  "/images/outside_08_t.jpg",
  "/images/aerials_01_t.jpg",
  "/images/aerials_02_t.jpg",
  "/images/aerials_03_t.jpg",
  "/images/aerials_04_t.jpg",
  "/images/aerials_05_t.jpg",
  "/images/food_01_t.jpg",
  "/images/food_02_t.jpg",
  "/images/food_03_t.jpg",
  "/images/food_04_t.jpg",
  "/images/food_05_t.jpg",
  "/images/food_06_t.jpg",
  "/images/food_07_t.jpg",
  "/images/food_08_t.jpg",
  "/images/food_09_t.jpg",
  "/images/food_10_t.jpg",
  "/images/people_01_t.jpg",
  "/images/people_02_t.jpg",
  "/images/people_03_t.jpg",
  "/images/wedding_01_t.jpg",
  "/images/wedding_02_t.jpg",
  "/images/wedding_03_t.jpg",
  "/images/wedding_04_t.jpg",
  "/images/wedding_05_t.jpg",
  "/images/wedding_06_t.jpg",
  "/images/wedding_07_t.jpg",
];

// Full-size image URLs (replace _t with full-size versions)
const fullSizePhotoUrls = photoUrls.map(url => url.replace('_t.jpg', '.jpg'));

export default function AboutMe() {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(3); // Start from first original image
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalPhotoIndex, setModalPhotoIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  const [previousPhotoIndex, setPreviousPhotoIndex] = useState(3);
  const [isCrossfading, setIsCrossfading] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(0);
  const [hasErrors, setHasErrors] = useState(false);
  const containerRef = useRef(null);

  // Create a looped array with some images duplicated at the beginning and end
  const loopedPhotoUrls = [
    ...photoUrls.slice(-3), // Last 3 images at the beginning
    ...photoUrls,           // All original images
    ...photoUrls.slice(0, 3) // First 3 images at the end
  ];

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight;
      setIsMobile(newWidth <= 768);
      setWindowSize({ width: newWidth, height: newHeight });
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Force re-render when container size changes
  useEffect(() => {
    if (!containerRef.current) return;
    
    const resizeObserver = new ResizeObserver(() => {
      // Force re-render by updating windowSize
      setWindowSize(prev => ({ ...prev }));
    });
    
    resizeObserver.observe(containerRef.current);
    
    return () => resizeObserver.disconnect();
  }, []);

  // Calculate responsive dimensions - simplified with object-fit: cover
  const getSliderDimensions = () => {
    const isMobileNow = windowSize.width <= 768;
    const mobileAspectRatio = 5850 / 2533; // New aspect ratio for mobile
    const desktopAspectRatio = 1080 / 156; // Original working aspect ratio for desktop
    
    if (isMobileNow && containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const sliderWidth = containerWidth * 0.8; // 80% of container width
      const calculatedHeight = sliderWidth / mobileAspectRatio;
      return { width: sliderWidth, height: calculatedHeight };
    }
    
    // Desktop: use container width to calculate height (original working logic)
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const maxSliderWidth = Math.min(1200, containerWidth * 0.95); // Back to simple calculation
      const calculatedHeight = maxSliderWidth / desktopAspectRatio;
      return { width: maxSliderWidth, height: calculatedHeight };
    }
    
    // Fallback
    return { width: 1080, height: 1080 / desktopAspectRatio };
  };

  const { width: sliderWidth, height: sliderHeight } = getSliderDimensions();
  const imageWidth = isMobile ? sliderWidth : Math.min(360, sliderWidth / 3); // Back to original width

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPhotoIndex((prevIndex) => {
        const newIndex = prevIndex + 1;
        // If we reach the end of the original images, reset to the start of the original images
        if (newIndex >= photoUrls.length + 3) {
          // Smoothly reset to the beginning
          setTimeout(() => {
            setCurrentPhotoIndex(3);
            setPreviousPhotoIndex(3);
            setIsCrossfading(false);
          }, 500); // Wait for transition to complete
          return photoUrls.length + 2; // Go to the last duplicated image
        }
        
        // Start crossfade transition
        setPreviousPhotoIndex(prevIndex);
        setIsCrossfading(true);
        
        // End crossfade after transition completes
        setTimeout(() => {
          setIsCrossfading(false);
        }, 500);
        
        return newIndex;
      });
    }, 3000); // Change photo every 3 seconds

    return () => clearInterval(interval);
  }, []);

  // Handle crossfade animation
  useEffect(() => {
    if (isCrossfading) {
      const timer = setTimeout(() => {
        setIsCrossfading(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isCrossfading]);

  const handlePhotoClick = () => {
    // Convert looped index to original index
    const originalIndex = currentPhotoIndex - 3;
    const modalIndex = originalIndex >= 0 ? originalIndex : photoUrls.length + originalIndex;
    setModalPhotoIndex(modalIndex);
    setIsModalOpen(true);
  };

  const handleImageLoad = useCallback(() => {
    setImagesLoaded(prev => prev + 1);
  }, []);

  const handleImageError = useCallback((e) => {
    e.target.style.display = "none";
    setHasErrors(true);
  }, []);

  // Check if we have enough images loaded to show the slider
  const isSliderReady = imagesLoaded >= Math.min(3, photoUrls.length) || hasErrors;

  return (
    <div
      id="about"
      className="bg-dark container-fluid"
      style={{ paddingTop: "15vh", paddingBottom: "15vh" }}
    >
      <Container style={{ maxWidth: "1000px" }} className="text-center p-3" ref={containerRef}>
        <div className="text-white">
          <Row xs={1} md={1} lg={1} className="g-5">
            <Col style={{ marginBottom: "8rem" }}>
              <div className="text-center align-middle">
                <p className="text-center align-middle display-1 p-3">
                  About Me
                  </p>
                <div 
                  style={{ 
                    width: `${sliderWidth}px`, 
                    height: `${sliderHeight}px`, 
                    margin: "0 auto 2rem auto",
                    borderRadius: "10px",
                    overflow: "hidden",
                    border: "2px solid #333",
                    position: "relative",
                    maskImage: isMobile ? "none" : "linear-gradient(to right, transparent 0%, transparent 15%, black 42.5%, black 57.5%, transparent 85%, transparent 100%)",
                    WebkitMaskImage: isMobile ? "none" : "linear-gradient(to right, transparent 0%, transparent 15%, black 42.5%, black 57.5%, transparent 85%, transparent 100%)",
                    cursor: isSliderReady ? "pointer" : "default"
                  }}
                  onClick={isSliderReady ? handlePhotoClick : undefined}
                >
                  {/* Loading state */}
                  {!isSliderReady && (
                    <div 
                      style={{ 
                        position: "absolute",
                        top: "0",
                        left: "0",
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#222",
                        zIndex: 10
                      }}
                    >
                      <div style={{ color: "#666", fontSize: "1.2em" }}>
                        Loading photos...
                      </div>
                    </div>
                  )}
                  
                  {/* Previous image (for crossfade) - only on mobile */}
                  {isCrossfading && isMobile && (
                    <div
                      style={{
                        position: "absolute",
                        left: `-${previousPhotoIndex * imageWidth}px`,
                        top: "0",
                        width: `${loopedPhotoUrls.length * imageWidth}px`,
                        height: `${sliderHeight}px`,
                        transition: "left 0.5s ease-in-out",
                        display: "flex",
                        zIndex: 1
                      }}
                    >
                      {loopedPhotoUrls.map((url, index) => (
                        <img
                          key={`prev-${index}`}
                          src={url}
                          alt={getPhotoCategory(url)}
                          style={{
                            width: `${imageWidth}px`,
                            height: `${sliderHeight}px`,
                            flexShrink: 0,
                            objectFit: "cover",
                            opacity: index === previousPhotoIndex ? "1" : "0"
                          }}
                          onLoad={handleImageLoad}
                          onError={handleImageError}
                        />
                      ))}
                    </div>
                  )}
                  
                  {/* Current image */}
                  <div
                    style={{
                      position: "absolute",
                      left: isMobile ? `-${currentPhotoIndex * imageWidth}px` : `-${(currentPhotoIndex * imageWidth) - imageWidth}px`,
                      top: "0",
                      width: `${loopedPhotoUrls.length * imageWidth}px`,
                      height: `${sliderHeight}px`,
                      transition: "left 0.5s ease-in-out",
                      display: "flex",
                      zIndex: isMobile && isCrossfading ? 2 : 1
                    }}
                  >
                    {loopedPhotoUrls.map((url, index) => (
                      <img
                        key={`current-${index}`}
                        src={url}
                        alt={getPhotoCategory(url)}
                        style={{
                          width: `${imageWidth}px`,
                          height: `${sliderHeight}px`,
                          flexShrink: 0,
                          objectFit: "cover",
                          opacity: index === currentPhotoIndex ? "1" : (isMobile ? "0" : "0.6")
                        }}
                        onLoad={handleImageLoad}
                        onError={handleImageError}
                      />
                    ))}
                  </div>
                  
                  {/* Fallback camera icon */}
                  <div 
                    style={{ 
                      display: "none",
                      position: "absolute",
                      left: `${imageWidth}px`,
                      top: "0",
                      width: `${imageWidth}px`,
                      height: `${sliderHeight}px`,
                      fontSize: isMobile ? "2em" : "4em",
                      lineHeight: `${sliderHeight}px`,
                      color: "#666",
                      zIndex: 4
                    }}
                  >
                    <i className="fas fa-camera-retro"></i>
                  </div>
                </div>
                <p>
                  I've been <a href={photosLink}>taking photos</a> for as long
                  as I can remember and my first website was hosted on
                  Geocities. I learned how to write HTML by reading a book at
                  the public library. I also coded it there.
                  <br />
                  (they had free internet access. In the 90s!)
                </p>
              </div>
            </Col>
            <Col style={{ marginBottom: "8rem" }}>
              <div className="text-center align-middle">
                <ReactP5Wrapper sketch={sketch} />
                <p>
                  Fast-forward to today:
                  <br /> I've worked in glamorous Hollywood{" "}
                  <a href={imdb}>managing data</a> for major motion pictures,{" "}
                  <a href={toldStories}>told stories</a> for a midwest ad
                  agency, and even developed and launched a lifestyle branded
                  website for my hometown.
                </p>
              </div>
            </Col>
            <Col>
              <div className="text-center align-middle">
                <ReactP5Wrapper sketch={sketch2} />
                <p>
                  I am currently a Developer at{" "}
                  <a href="http://carmichaellynch.com">Carmichael Lynch</a>, 
                  where every day I help bring big ideas to life through code and creativity.
                  <br />
                  Outside of work, I am focused on{" "}
                  <a href="https://pizzadeliverysurvivors.com">Pizza Delivery Survivors</a> as a solo dev. 
                  It is an apocalyptic pizza delivery game built with Godot 4, 
                  available now on <a href="https://pizzadeliverysurvivors.com">Steam Early Access for $9.99</a>.
                  <br /><br />
                  I also continue to build
                  <a href={interactiveExperiences}>interactive experiences</a>{" "}
                  and <a href={whimsical}>whimsical hardware</a> projects.
                </p>
              </div>
            </Col>
          </Row>
        </div>
      </Container>

      {/* Photo Modal */}
      {isModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer"
          }}
          onClick={() => setIsModalOpen(false)}
        >
          <div
            style={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh",
              cursor: "default"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={fullSizePhotoUrls[modalPhotoIndex]}
              alt={getPhotoCategory(fullSizePhotoUrls[modalPhotoIndex])}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain"
              }}
              onError={handleImageError}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Future enhancement: This function could be replaced with AI image analysis
// const generateAIDescriptiveAltText = async (imageUrl) => {
//   // Integration with AI services like Google Vision API, Azure Computer Vision, etc.
//   // This would provide actual image content analysis
//   return await aiService.analyzeImage(imageUrl);
// };
